'use client';

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ArrowLeft, ArrowRight, X } from 'lucide-react';
import { cn } from '@sharpr/ui';
import { useTour } from './TourContext';
import type { TourPlacement, TourStep } from './types';

// Tunables — kept hoisted so referential identity stays stable across renders.
const SPOTLIGHT_PADDING = 8;
const SPOTLIGHT_RADIUS = 12;
const POPOVER_GAP = 12;
const POPOVER_WIDTH = 320;
const MOBILE_BREAKPOINT = 768;
const TARGET_POLL_MS = 80;
const TARGET_TIMEOUT_MS = 4000;

interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}

/** Bounding box that encloses every element. For a single element it's just
 *  that element's rect; for several (eg. a player's avatar + name) it's their
 *  union, so the spotlight cuts one hole over the whole group. */
function unionRectFrom(els: Element[]): Rect | null {
  let top = Infinity;
  let left = Infinity;
  let right = -Infinity;
  let bottom = -Infinity;
  for (const el of els) {
    const r = el.getBoundingClientRect();
    top = Math.min(top, r.top);
    left = Math.min(left, r.left);
    right = Math.max(right, r.right);
    bottom = Math.max(bottom, r.bottom);
  }
  if (!Number.isFinite(top)) return null;
  return { top, left, width: right - left, height: bottom - top };
}

function rectsEqual(a: Rect | null, b: Rect | null): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    Math.round(a.top) === Math.round(b.top) &&
    Math.round(a.left) === Math.round(b.left) &&
    Math.round(a.width) === Math.round(b.width) &&
    Math.round(a.height) === Math.round(b.height)
  );
}

/** Identity-wise equality of two element lists (order-sensitive). Lets the
 *  target hooks hold a referentially-stable array across renders, so it can be
 *  used directly as an effect dependency. */
function sameElements(a: Element[], b: Element[]): boolean {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

/** Returns true when an element is currently rendered into the document AND
 *  not hidden via display:none anywhere in its ancestor chain. The cheap
 *  `offsetParent !== null` trick covers display:none on the element or any
 *  ancestor — exactly what we need to skip the always-mounted-but-hidden
 *  desktop/mobile siblings (`hidden md:flex` etc.). */
function isElementVisible(el: Element | null): boolean {
  if (!el) return false;
  if (!(el instanceof HTMLElement)) return true;
  if (el.offsetParent === null && getComputedStyle(el).position !== 'fixed') return false;
  const rect = el.getBoundingClientRect();
  return rect.width > 0 && rect.height > 0;
}

/** Resolves the DOM elements for a step's target. Returns an empty array if no
 *  target (overlay-style step) or if none are mounted yet. Usually one element,
 *  but a step may tag several elements with the same data-tour to spotlight them
 *  together (eg. a player's avatar + name for the research step) — the spotlight
 *  unions their rects. Responsive alternatives (a desktop row + a mobile button
 *  both tagged the same) collapse to the single *visible* one, since the hidden
 *  sibling is filtered out. */
function useTargetElements(targetId: string | undefined): Element[] {
  const [els, setEls] = useState<Element[]>([]);

  const findVisible = useCallback((): Element[] => {
    const matches = document.querySelectorAll(`[data-tour="${targetId}"]`);
    const visible: Element[] = [];
    for (const match of matches) {
      if (isElementVisible(match)) visible.push(match);
    }
    return visible;
  }, [targetId]);

  // Commit only when the set actually changes so `els` stays referentially
  // stable — callers depend on it directly in effect deps.
  const commit = useCallback(
    (next: Element[]) => setEls((prev) => (sameElements(prev, next) ? prev : next)),
    [],
  );

  useEffect(() => {
    if (!targetId) {
      setEls([]);
      return;
    }
    const initial = findVisible();
    // Always set — clearing when the new target isn't mounted yet prevents the
    // spotlight from lingering on the previous step's element (eg. stepping Back
    // from player-research to line-movement before the demo card's onEnter has
    // finished re-expanding it).
    commit(initial);
    if (initial.length) return;
    // Poll briefly while routes settle / lists hydrate. Times out at 4s.
    let cancelled = false;
    const started = Date.now();
    const tick = () => {
      if (cancelled) return;
      const found = findVisible();
      if (found.length) {
        commit(found);
        return;
      }
      if (Date.now() - started > TARGET_TIMEOUT_MS) return;
      window.setTimeout(tick, TARGET_POLL_MS);
    };
    window.setTimeout(tick, TARGET_POLL_MS);

    return () => {
      cancelled = true;
    };
  }, [targetId, findVisible, commit]);

  // Re-find when the set changes mid-step (eg. route changed, list rerendered,
  // or visibility flipped because the user resized into a different breakpoint
  // while the tour is mid-flight). The equality guard in commit() keeps this
  // from churning state when nothing moved.
  useEffect(() => {
    if (!targetId) return;
    const interval = window.setInterval(() => {
      // Cheap per-tick check: are the tracked elements still connected + visible? Only fall
      // back to the full querySelectorAll re-scan when one vanished/changed. Re-scanning the
      // whole DOM (+ getBoundingClientRect on every match) 4×/sec for the step's lifetime
      // forces needless synchronous reflow even when nothing moved.
      const stillValid = els.length > 0 && els.every((el) => el.isConnected && isElementVisible(el));
      if (!stillValid) commit(findVisible());
    }, 250);
    return () => window.clearInterval(interval);
  }, [targetId, els, findVisible, commit]);

  return els;
}

/** Tracks the union viewport rect of a set of elements, updating on scroll,
 *  resize, and DOM mutations. Empty set → null. */
function useUnionRect(els: Element[]): Rect | null {
  const [rect, setRect] = useState<Rect | null>(null);

  useLayoutEffect(() => {
    if (els.length === 0) {
      setRect(null);
      return;
    }
    const measure = () => {
      const next = unionRectFrom(els);
      setRect((prev) => (rectsEqual(prev, next) ? prev : next));
    };
    measure();

    const ro = new ResizeObserver(measure);
    els.forEach((el) => ro.observe(el));
    window.addEventListener('scroll', measure, true);
    window.addEventListener('resize', measure);

    // The target can move without resizing (eg. a sibling collapses). Poll
    // lightly as a safety net; the equality guard keeps state stable.
    const interval = window.setInterval(measure, 400);

    return () => {
      ro.disconnect();
      window.removeEventListener('scroll', measure, true);
      window.removeEventListener('resize', measure);
      window.clearInterval(interval);
    };
    // `els` is referentially stable across renders (see useTargetElements).
  }, [els]);

  return rect;
}

/** True when viewport width is under the md breakpoint. */
function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`);
    const update = () => setIsMobile(mql.matches);
    update();
    mql.addEventListener('change', update);
    return () => mql.removeEventListener('change', update);
  }, []);
  return isMobile;
}

interface PopoverPosition {
  top: number;
  left: number;
  placement: TourPlacement;
}

function pickPopoverPosition(
  rect: Rect | null,
  preferred: TourPlacement,
  viewport: { width: number; height: number },
  estimatedHeight: number,
): PopoverPosition {
  if (!rect) {
    return {
      top: Math.max(16, viewport.height - estimatedHeight - 24),
      left: Math.max(16, viewport.width - POPOVER_WIDTH - 24),
      placement: 'center',
    };
  }

  const margin = 16;
  const spaceBelow = viewport.height - (rect.top + rect.height) - margin;
  const spaceAbove = rect.top - margin;
  const spaceRight = viewport.width - (rect.left + rect.width) - margin;
  const spaceLeft = rect.left - margin;

  // Fall back to whichever side has the most room when preferred doesn't fit.
  const order: TourPlacement[] = (() => {
    switch (preferred) {
      case 'bottom':
        return ['bottom', 'top', 'right', 'left'];
      case 'top':
        return ['top', 'bottom', 'right', 'left'];
      case 'left':
        return ['left', 'right', 'bottom', 'top'];
      case 'right':
        return ['right', 'left', 'bottom', 'top'];
      default:
        return ['bottom', 'top', 'right', 'left'];
    }
  })();

  for (const placement of order) {
    if (placement === 'bottom' && spaceBelow >= estimatedHeight) {
      const left = Math.min(
        Math.max(margin, rect.left + rect.width / 2 - POPOVER_WIDTH / 2),
        viewport.width - POPOVER_WIDTH - margin,
      );
      return { top: rect.top + rect.height + POPOVER_GAP, left, placement };
    }
    if (placement === 'top' && spaceAbove >= estimatedHeight) {
      const left = Math.min(
        Math.max(margin, rect.left + rect.width / 2 - POPOVER_WIDTH / 2),
        viewport.width - POPOVER_WIDTH - margin,
      );
      return { top: rect.top - estimatedHeight - POPOVER_GAP, left, placement };
    }
    if (placement === 'right' && spaceRight >= POPOVER_WIDTH) {
      const top = Math.min(
        Math.max(margin, rect.top + rect.height / 2 - estimatedHeight / 2),
        viewport.height - estimatedHeight - margin,
      );
      return { top, left: rect.left + rect.width + POPOVER_GAP, placement };
    }
    if (placement === 'left' && spaceLeft >= POPOVER_WIDTH) {
      const top = Math.min(
        Math.max(margin, rect.top + rect.height / 2 - estimatedHeight / 2),
        viewport.height - estimatedHeight - margin,
      );
      return { top, left: rect.left - POPOVER_WIDTH - POPOVER_GAP, placement };
    }
  }

  // Nothing fits — center on screen.
  return {
    top: Math.max(margin, viewport.height / 2 - estimatedHeight / 2),
    left: Math.max(margin, viewport.width / 2 - POPOVER_WIDTH / 2),
    placement: 'center',
  };
}

/** Nearest scrollable ancestor (the virtualized player list on mobile), or null
 *  when the element scrolls with the page. */
function getScrollParent(el: Element): HTMLElement | null {
  let node = el.parentElement;
  while (node) {
    const { overflowY } = getComputedStyle(node);
    if ((overflowY === 'auto' || overflowY === 'scroll') && node.scrollHeight > node.clientHeight) {
      return node;
    }
    node = node.parentElement;
  }
  return null;
}

/** Smooth-scrolls the target into view if it isn't already comfortably visible.
 *  `obstructedBottomPx` is the height of any fixed overlay pinned to the bottom
 *  of the screen — the mobile tour popover. We treat that band as obstructed so
 *  a target hiding *behind* the popover still counts as off-screen (otherwise it
 *  reads as "visible" and never scrolls), and we re-center it in the clear band
 *  above the popover rather than the full viewport (which would leave it behind
 *  the popover — the "card opens behind it" bug on short phones). */
function useScrollIntoView(el: Element | null, obstructedBottomPx: number) {
  // Read the obstruction through a ref so this only scrolls when the target
  // (el) changes — not again when the popover settles from its estimated to its
  // measured height (which would double-scroll the target on short phones).
  const obstructedRef = useRef(obstructedBottomPx);
  obstructedRef.current = obstructedBottomPx;
  useEffect(() => {
    if (!el) return;
    const obstructedBottomPx = obstructedRef.current;
    const r = el.getBoundingClientRect();
    const safeBottom = window.innerHeight - obstructedBottomPx;
    const fullyVisible =
      r.top >= 0 && r.left >= 0 && r.bottom <= safeBottom && r.right <= window.innerWidth;
    if (fullyVisible) return;

    if (obstructedBottomPx > 0) {
      const scrollParent = getScrollParent(el);
      if (scrollParent) {
        // Center the target in the clear band between the list's top and the
        // popover, so the expanded card's spotlit row sits above the popover.
        const clearTop = scrollParent.getBoundingClientRect().top;
        const clearCenter = (clearTop + safeBottom) / 2;
        const delta = r.top + r.height / 2 - clearCenter;
        scrollParent.scrollBy({ top: delta, behavior: 'smooth' });
        return;
      }
    }
    el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
    // obstructedBottomPx read via ref — intentionally not a dep (see above).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [el]);
}

interface SpotlightProps {
  rect: Rect | null;
  hasTarget: boolean;
}

function Spotlight({ rect, hasTarget }: SpotlightProps) {
  if (!hasTarget) {
    // Overlay-style step (eg. line movement) — the feature it demonstrates
    // already paints its own dim/drawer. Suppress our backdrop so the user
    // sees the actual overlay underneath, not a doubled-up dim.
    return null;
  }
  if (!rect) {
    // Has a target that hasn't measured yet — full dim while we wait for it
    // to mount (cross-route step or list still hydrating).
    return (
      <div
        aria-hidden="true"
        className="fixed inset-0 z-[9998] bg-black/60 backdrop-blur-[2px] pointer-events-auto"
      />
    );
  }
  // Four dim "frame" divs surround the cutout. The gap between them is a real
  // hole in the DOM, so clicks on the spotlit element fall through to it —
  // critical for steps that ask the user to actually tap the highlighted
  // control (eg. mobile line-movement: tap a book in the per-book breakdown).
  // Outside-the-cutout clicks still hit a frame and are absorbed.
  const top = rect.top - SPOTLIGHT_PADDING;
  const left = rect.left - SPOTLIGHT_PADDING;
  const width = rect.width + SPOTLIGHT_PADDING * 2;
  const height = rect.height + SPOTLIGHT_PADDING * 2;
  const dimClass = 'fixed bg-black/65 pointer-events-auto z-[9998] transition-[top,left,width,height] duration-200 ease-out';
  return (
    <>
      <div aria-hidden="true" className={dimClass}
        style={{ top: 0, left: 0, right: 0, height: Math.max(0, top) }} />
      <div aria-hidden="true" className={dimClass}
        style={{ top: top + height, left: 0, right: 0, bottom: 0 }} />
      <div aria-hidden="true" className={dimClass}
        style={{ top, left: 0, width: Math.max(0, left), height }} />
      <div aria-hidden="true" className={dimClass}
        style={{ top, left: left + width, right: 0, height }} />
      <div
        aria-hidden="true"
        className="fixed pointer-events-none z-[9998] transition-[top,left,width,height] duration-200 ease-out"
        style={{
          top,
          left,
          width,
          height,
          borderRadius: SPOTLIGHT_RADIUS,
          outline: '2px solid rgba(59, 130, 246, 0.6)',
          outlineOffset: 2,
        }}
      />
    </>
  );
}

interface PopoverProps {
  step: TourStep;
  index: number;
  total: number;
  position: PopoverPosition;
  onBack: () => void;
  onNext: () => void;
  onSkip: () => void;
  isMobile: boolean;
  /** Fires whenever the popover's rendered height changes so the parent can
   *  reposition it with a real measurement instead of the conservative
   *  estimate — without this, long bodies overflow the viewport on
   *  no-target steps. */
  onHeightChange?: (height: number) => void;
}

function Popover({ step, index, total, position, onBack, onNext, onSkip, isMobile, onHeightChange }: PopoverProps) {
  const isLast = index === total - 1;
  const isFirst = index === 0;
  const cardRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const el = cardRef.current;
    if (!el || !onHeightChange) return;
    const report = () => onHeightChange(el.getBoundingClientRect().height);
    report();
    const observer = new ResizeObserver(report);
    observer.observe(el);
    return () => observer.disconnect();
  }, [onHeightChange]);

  const card = (
    <div
      ref={cardRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby="sharpr-tour-title"
      className={cn(
        'rounded-xl bg-white dark:bg-bg-secondary border border-gray-200 dark:border-border-primary',
        'shadow-2xl ring-1 ring-black/5 dark:ring-white/5',
        'p-4 sm:p-5',
        'flex flex-col gap-3',
      )}
      style={{ width: isMobile ? 'auto' : POPOVER_WIDTH }}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-blue-600 dark:text-blue-400 mb-1">
            Step {index + 1} of {total}
          </p>
          <h2
            id="sharpr-tour-title"
            className="text-sm sm:text-base font-semibold text-fg dark:text-text-primary"
          >
            {step.title}
          </h2>
        </div>
        <button
          type="button"
          onClick={onSkip}
          aria-label="Skip walkthrough"
          className="flex-shrink-0 p-1 -m-1 rounded-md text-gray-400 dark:text-text-tertiary hover:text-fg dark:hover:text-text-primary hover:bg-muted/50 dark:hover:bg-bg-overlay transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <p className="text-sm leading-relaxed text-muted-foreground dark:text-text-secondary">
        {step.body}
      </p>

      {step.cta && (
        // Plain anchor (full nav) — leaving the page tears down the tour. onSkip
        // first so state is cleared before navigation.
        <a
          href={step.cta.href}
          onClick={onSkip}
          className="inline-flex items-center justify-center w-full px-3 py-2 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors shadow-sm"
        >
          {step.cta.label}
        </a>
      )}

      {step.links && step.links.length > 0 && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
          {step.links.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={onSkip}
              className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 dark:text-blue-400 hover:underline"
            >
              {link.label}
              <ArrowRight className="w-3 h-3" />
            </a>
          ))}
        </div>
      )}

      <div className="flex items-center justify-between gap-2 pt-1">
        <button
          type="button"
          onClick={onSkip}
          className="text-xs font-medium text-gray-400 dark:text-text-tertiary hover:text-fg dark:hover:text-text-primary transition-colors"
        >
          Skip tour
        </button>
        <div className="flex items-center gap-2">
          {!isFirst && (
            <button
              type="button"
              onClick={onBack}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-fg dark:text-text-primary border border-border dark:border-border-primary rounded-md hover:bg-muted/50 dark:hover:bg-bg-overlay transition-colors"
            >
              <ArrowLeft className="w-3 h-3" />
              Back
            </button>
          )}
          <button
            type="button"
            onClick={onNext}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors shadow-sm"
          >
            {isLast ? 'Done' : (
              <>
                Next
                <ArrowRight className="w-3 h-3" />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );

  if (isMobile) {
    return (
      <div
        className="fixed left-3 right-3 bottom-3 z-[9999] pointer-events-auto"
        style={{ pointerEvents: 'auto' }}
      >
        {card}
      </div>
    );
  }

  return (
    <div
      className="fixed z-[9999] pointer-events-auto transition-[top,left] duration-200 ease-out"
      style={{ top: position.top, left: position.left, width: POPOVER_WIDTH }}
    >
      {card}
    </div>
  );
}

export function TourOverlay() {
  const tour = useTour();
  const step = tour.step;
  const targetEls = useTargetElements(step?.targetId);
  const rect = useUnionRect(targetEls);
  // First element in DOM order anchors scroll-into-view; the others (eg. an
  // adjacent name beside an avatar) ride along since they share a row.
  const targetEl = targetEls[0] ?? null;
  const isMobile = useIsMobile();

  const [viewport, setViewport] = useState({ width: 0, height: 0 });
  useEffect(() => {
    const update = () =>
      setViewport({ width: window.innerWidth, height: window.innerHeight });
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  // Measured popover height (reset between steps so a long step doesn't
  // hand its height to the next short step before that step measures).
  // Falls back to a conservative pre-measurement estimate.
  const [measuredHeight, setMeasuredHeight] = useState<number | null>(null);
  useEffect(() => {
    setMeasuredHeight(null);
  }, [step?.id]);
  const handleHeightChange = useCallback(
    (h: number) => setMeasuredHeight((prev) => (prev != null && Math.abs(prev - h) < 1 ? prev : h)),
    [],
  );
  const estimatedHeight = measuredHeight ?? 320;
  // On mobile the popover is pinned to the bottom (`bottom-3`), so the band it
  // covers — its height plus the 12px inset and a 12px breathing gap — is
  // obstructed. Scroll spotlight targets into the clear region above it.
  useScrollIntoView(targetEl, isMobile ? estimatedHeight + 12 + POPOVER_GAP : 0);
  const position = useMemo(
    () =>
      pickPopoverPosition(
        rect,
        step?.placement ?? 'bottom',
        viewport,
        estimatedHeight,
      ),
    [rect, step?.placement, viewport, estimatedHeight],
  );

  if (!tour.active || !step) return null;
  if (typeof document === 'undefined') return null;

  const hasTarget = !!step.targetId;

  return createPortal(
    <>
      <Spotlight rect={rect} hasTarget={hasTarget} />
      <Popover
        step={step}
        index={tour.index}
        total={tour.steps.length}
        position={position}
        onBack={tour.back}
        onNext={tour.next}
        onSkip={tour.skip}
        isMobile={isMobile}
        onHeightChange={handleHeightChange}
      />
    </>,
    document.body,
  );
}
